import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ISubLevel } from '@GFTypes/GameStoreTypes';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { StateMachine } from '@Managers/StateMachine/StateMachine';
import { BaseWinLevelsConfigParser } from './BaseWinLevelsConfigParser';
import { BaseWinPresentationSpine } from './BaseWinPresentationSpine';
import Odometer from './Elements/Odometer';
import ParticleEmitter, { TParticleEmitterConfig } from './Elements/ParticleEmitter';
import { WinPresentationStateConditions } from './States/WinPresentationStateConditions';
import { EDebugWinPresentationEvents, EWinPresentationStates } from './States/WinPresentationStates';
import { WinPresentationStatesDefault } from './States/WinPresentationStatesDefault';
import { WIN_PRESENTATION_ANIMATION_STATES, WIN_PRESENTATION_STEPS_CONFIG } from './WinPresentation.constants';
import {
    EWaitFor,
    IOdometerCheckpoint,
    IWinConfiguration,
    IWinPresentationConfig,
    IWinPresentationStep,
    IWinPresentationStepExecutor,
    IWinPresentationStepsConfig,
    TWinPresentationAnimationRelatedStates
} from './WinPresentation.types';
import { EventSubscription } from './WinPresentationEventSubscriptionsBuilder';
import { WinPresentationPromises } from './WinPresentationPromises';
import { WinPresentationStepExecutor } from './WinPresentationStepExecutor';

export class WinPresentation extends GameComponent {
    public winPresentationPromises: WinPresentationPromises;
    private winPresentationStateMachine: StateMachine<EWinPresentationStates>;
    public winConfig: IWinConfiguration;
    public odometer: Odometer;
    public activeWinSpine: SpineWrapper;
    public activeOdometerSlotName: string;
    private currentParticleEmitter?: ParticleEmitter;
    private readonly particleEmitters: ParticleEmitter[] = [];
    private stepExecutor: IWinPresentationStepExecutor;
    private currentSubLevelIndex: number = -1;
    private currentWinPresentationStepConfig: IWinPresentationStepsConfig = WIN_PRESENTATION_STEPS_CONFIG;
    private odometerHoldDuration: number = 0;
    private currentSublevels: ISubLevel[];
    private readonly eventSubscriptions: { event: string | symbol; fn: Function; context?: any }[] = [];
    private winLevelsParser?: BaseWinLevelsConfigParser;
    private readonly defaultWinSpine: SpineWrapper;
    private readonly defaultOdometerSlotName: string;
    private odometerEvents: IOdometerCheckpoint[];

    constructor(
        public scene: GameScene,
        winSpine: SpineWrapper,
        odometerSlotName: string = BaseWinPresentationSpine.ODOMETER_SLOT_NAME
    ) {
        super(scene);
        Logger.log(LoggerLevel.Constructor, { class: WinPresentation.name, source: LoggerSource.Game, message: 'Win constructor' });
        this.defaultWinSpine = winSpine;
        this.activeWinSpine = winSpine;
        this.defaultOdometerSlotName = odometerSlotName;
        this.activeOdometerSlotName = odometerSlotName;
        EventsCenter.on(EWinPresentationStates.WIN_PRESENTATION_HIDDEN_STATE, this.onWinPresentationHidden, this);
        EventsCenter.on(EWinPresentationStates.PRE_WIN_PRESENTATION_STATE, this.onPreWinPresentationState, this);
        EventsCenter.on(EWinPresentationStates.WIN_PRESENTATION_IN_STATE, this.onWinPresentationInState, this);
        EventsCenter.on(EWinPresentationStates.WIN_PRESENTATION_IDLE_STATE, this.onWinPresentationIdleState, this);
        EventsCenter.on(EWinPresentationStates.WIN_PRESENTATION_HOLD_STATE, this.onWinPresentationHoldState, this);
        EventsCenter.on(EWinPresentationStates.WIN_PRESENTATION_OUT_STATE, this.onWinPresentationOutState, this);
        EventsCenter.on(EWinPresentationStates.POST_WIN_PRESENTATION_STATE, this.onPostWinPresentationState, this);
    }

    public async startWin(bet: number, totalWin: number, config?: IWinPresentationConfig): Promise<void> {
        this.terminateWinPresentation();
        await this.winPresentationPromises.winHidden.promise;
        if (config?.winPresentationStepsConfig) this.currentWinPresentationStepConfig = config.winPresentationStepsConfig;
        if (config?.odometerHoldDuration) this.odometerHoldDuration = config.odometerHoldDuration;
        if (config?.winPresentationSubLevels)
            this.currentSublevels = config?.winPresentationSubLevels.toSorted((sublevel, nextSublevel) => (sublevel.from ?? 0) - (nextSublevel.from ?? 0));
        else if (this.winLevelsParser) {
            const winLevel = this.winLevelsParser.getWinLevels().find((level) => level.to * bet > totalWin);
            this.currentSublevels = winLevel?.subLevels ?? [];
            this.odometerHoldDuration = winLevel?.odometerDuration ?? 0;
        }
        if (config?.spine && config.spine !== this.activeWinSpine) {
            this.activeWinSpine.slotManager.unassignObjectFromSlot(this.activeOdometerSlotName);
            this.activeOdometerSlotName = config?.odometerSlotName ?? this.defaultOdometerSlotName;
            this.activeWinSpine = config?.spine ?? this.defaultWinSpine;
            this.activeWinSpine?.slotManager.removeAttachmentFromSlot(this.activeOdometerSlotName);
            this.activeWinSpine?.slotManager.assignObjectToSlot(this.activeOdometerSlotName, this.odometer.getContainer());
        }
        this.odometerEvents = config?.odometerCheckpoints ?? [];

        this.gameStore.set(
            EGameStoreKeys.WinPresentation,
            { bet, totalWin, finalSublevelName: this.currentSublevels.at(-1)?.name ?? '', finalSublevelIndex: this.currentSublevels.length - 1 },
            true
        );
        this.winPresentationPromises.winHidden.reset();
        this.winPresentationPromises.winStarted.resolve();
        WinPresentationStateConditions.winPresentationHiddenCondition.resolve();
        EventsCenter.emit(EDebugWinPresentationEvents.DEBUG_WINPRESENTATION_START_PROCESSED);
    }

    public setDefaultParser(winLevelsParser: BaseWinLevelsConfigParser) {
        this.winLevelsParser = winLevelsParser;
    }

    private createStateMachine(): void {
        this.winPresentationStateMachine = new StateMachine<EWinPresentationStates>(WinPresentationStatesDefault, WinPresentationStateConditions, {
            onStateChange: (state) => {
                this.gameStore.set(EGameStoreKeys.WinPresentation, { currentState: state }, true);
            }
        });
        this.winPresentationStateMachine.setGameState(EWinPresentationStates.WIN_PRESENTATION_HIDDEN_STATE);
    }

    public onInit(): void {
        this.gameStore = this.scene.game.gameStore;
        this.winConfig = this.gameStore.get(EGameStoreKeys.WinConfiguration);
    }

    public onCreate(): void {
        this.createOdometer();
        this.winPresentationPromises = new WinPresentationPromises(this.scene);
        this.createStepExecutor();
        this.createStateMachine();
    }

    public isInsideCheckpoint(): boolean {
        return this.odometer.getIsInsideCheckpoint();
    }

    private createOdometer(): void {
        this.odometer = new Odometer(this.scene, { ...this.winConfig.odometer! });
        this.activeWinSpine?.slotManager.removeAttachmentFromSlot(this.activeOdometerSlotName);
        this.activeWinSpine?.slotManager.assignObjectToSlot(this.activeOdometerSlotName, this.odometer.getContainer());
    }

    private createStepExecutor() {
        this.stepExecutor = new WinPresentationStepExecutor(this, (configs) => {
            this.currentParticleEmitter?.stop();
            this.currentParticleEmitter = new ParticleEmitter(this.scene, configs);
            this.particleEmitters.push(this.currentParticleEmitter);
            return this.currentParticleEmitter;
        });
    }

    private prepareWinPresentationStep(state: EWinPresentationStates, currentSublevel: ISubLevel): IWinPresentationStep {
        const { bet, totalWin, currentState } = this.gameStore.get(EGameStoreKeys.WinPresentation);

        const currentSublevelName = currentSublevel.name;
        const currentWinPresentationStepConfig = WIN_PRESENTATION_ANIMATION_STATES.has(state)
            ? this.currentWinPresentationStepConfig.sublevels[currentSublevelName]?.[state as TWinPresentationAnimationRelatedStates]
            : this.currentWinPresentationStepConfig[state as Exclude<EWinPresentationStates, TWinPresentationAnimationRelatedStates>];
        let {
            eventToEmit,
            waitFor,
            skippable,
            odometer: { tween: shouldStartTween, font: shouldChangeFont } = {},
            timer: shouldStartTimer,
            particleEmitter: { start: shouldStartParticleEmitter, stop: shouldStopParticleEmitter } = {},
            stopSkipping
        } = currentWinPresentationStepConfig ?? {};

        if (this.currentSubLevelIndex === 0 && currentState === EWinPresentationStates.WIN_PRESENTATION_IN_STATE) shouldStartTween = true;
        const subLevel = this.currentSublevels[this.currentSubLevelIndex];
        const nextSubLevel = this.currentSublevels[this.currentSubLevelIndex + 1];

        let odometer: IWinPresentationStep['odometer'] | undefined = undefined;
        if (subLevel && (shouldStartTween || shouldChangeFont)) {
            const targetValue = nextSubLevel ? bet * nextSubLevel.from : totalWin;
            const currentOdometerValue = this.odometer.getValue() ?? 0;
            const checkpoints = this.odometerEvents.filter((event) => event.value >= currentOdometerValue && event.value <= targetValue);
            odometer = {
                ...(shouldStartTween && {
                    tween: {
                        targetValue,
                        duration: subLevel.odometerDuration ?? 0,
                        checkpoints
                    }
                }),
                ...(shouldChangeFont && {
                    font: {
                        key: subLevel.odometerFontKey ?? this.winConfig.odometer?.fontKey,
                        size: subLevel.odometerFontSize ?? this.winConfig.odometer?.fontSize
                    }
                })
            };
        }

        let timer: IWinPresentationStep['timer'] | undefined = undefined;
        if (shouldStartTimer) {
            timer = {
                duration: subLevel.duration ?? this.odometerHoldDuration
            };
        }

        let particleEmitterConfigs: TParticleEmitterConfig[] | undefined = undefined;
        if (shouldStopParticleEmitter) particleEmitterConfigs = [];
        else if (subLevel?.particleEmitters && shouldStartParticleEmitter) {
            particleEmitterConfigs = subLevel.particleEmitters
                .map((name) => this.winConfig.particleEmitters?.find((c) => c.name === name))
                .filter((c): c is TParticleEmitterConfig => c !== undefined);
        }

        eventToEmit ??= this.getDefaultEventToEmit(currentSublevelName, currentState);

        return this.createWinPresentationStep(state, waitFor ?? EWaitFor.NONE, skippable ?? false, {
            odometer,
            timer,
            particleEmitterConfigs,
            stopSkipping,
            eventToEmit
        });
    }

    private getDefaultEventToEmit(currentSubLevelName: string, currentState: EWinPresentationStates): EGameEvents | undefined {
        switch (currentState) {
            case EWinPresentationStates.WIN_PRESENTATION_IN_STATE:
                return `${currentSubLevelName}_IN` as EGameEvents;

            case EWinPresentationStates.WIN_PRESENTATION_OUT_STATE:
                return `${currentSubLevelName}_OUT` as EGameEvents;

            case EWinPresentationStates.WIN_PRESENTATION_IDLE_STATE:
                return currentSubLevelName as EGameEvents;

            default:
                return undefined;
        }
    }

    private createWinPresentationStep(
        state: EWinPresentationStates,
        waitFor: EWaitFor,
        skippable: boolean,
        { eventToEmit, stopSkipping, odometer, timer, particleEmitterConfigs }: Omit<Partial<IWinPresentationStep>, 'waitFor'> = {}
    ): IWinPresentationStep {
        return {
            state,
            waitFor,
            skippable,
            eventToEmit,
            stopSkipping,
            odometer,
            timer,
            particleEmitterConfigs
        };
    }

    private onWinPresentationHidden(): void {
        if (this.activeWinSpine?.spine) {
            this.activeWinSpine.spine.visible = false;
        }
        this.currentParticleEmitter?.destroy();
        this.winPresentationPromises.stopTimer();
        this.winPresentationPromises.winHidden.resolve();
        this.currentSubLevelIndex = -1;
        this.gameStore.set(EGameStoreKeys.WinPresentation, { currentSublevelIndex: this.currentSubLevelIndex }, true);
    }

    public skipWinPresentation(): void {
        this.gameStore.set(EGameStoreKeys.WinPresentation, { isSkipped: true }, true);
        this.winPresentationPromises.skipTriggered.resolve();
    }

    public terminateWinPresentation(): void {
        this.gameStore.set(EGameStoreKeys.WinPresentation, { isTerminated: true }, true);
        this.winPresentationPromises.skipTriggered.resolve();
    }

    private onPreWinPresentationState(): void {
        if (this.activeWinSpine?.spine) {
            this.activeWinSpine.spine.visible = true;
        }

        this.winPresentationPromises.betSaved.reset();
        this.winPresentationPromises.skipTriggered.reset();
        this.odometer?.hide();
        this.gameStore.set(EGameStoreKeys.WinPresentation, { isSkipped: false, isTerminated: false }, true);
    }

    private onWinPresentationInState(): void {
        this.currentSubLevelIndex++;
        this.gameStore.set(EGameStoreKeys.WinPresentation, { currentSublevelIndex: this.currentSubLevelIndex }, true);
        this.gameStore.set(EGameStoreKeys.WinPresentation, { currentState: this.winPresentationStateMachine.getCurrentState() }, true);
        const currentSublevelName = this.currentSublevels[this.currentSubLevelIndex].name;
        this.gameStore.set(EGameStoreKeys.WinPresentation, { currentSublevelName }, true);

        this.runStateStep(EWinPresentationStates.WIN_PRESENTATION_IN_STATE, '_IN', WinPresentationStateConditions.winPresentationInCondition);
    }

    private onWinPresentationIdleState(): void {
        const { isSkipped } = this.gameStore.get(EGameStoreKeys.WinPresentation);
        if (isSkipped) this.currentSubLevelIndex = this.currentSublevels.length - 1;
        const currentSublevelName = this.currentSublevels[this.currentSubLevelIndex].name;
        this.gameStore.set(EGameStoreKeys.WinPresentation, { currentSublevelName, currentSublevelIndex: this.currentSubLevelIndex }, true);

        this.runStateStep(EWinPresentationStates.WIN_PRESENTATION_IDLE_STATE, '', WinPresentationStateConditions.winPresentationIdleCondition);
    }

    private onWinPresentationHoldState(): void {
        this.odometer?.skipToEnd();
        this.currentParticleEmitter?.stop();

        this.runStateStep(EWinPresentationStates.WIN_PRESENTATION_HOLD_STATE, '_HOLD', WinPresentationStateConditions.winPresentationHoldCondition);
    }

    private onWinPresentationOutState(): void {
        this.runStateStep(EWinPresentationStates.WIN_PRESENTATION_OUT_STATE, '_OUT', WinPresentationStateConditions.winPresentationOutCondition);
    }

    private onPostWinPresentationState(): void {
        this.runStateStep(EWinPresentationStates.POST_WIN_PRESENTATION_STATE, '_END');

        this.odometer?.hide();
        this.winPresentationPromises.winStarted.reset();
    }

    public assignEventSubscriptions(...subscriptions: EventSubscription[]): void {
        this.eventSubscriptions.push(...subscriptions);
        for (const { event, fn, context } of subscriptions) {
            EventsCenter.on(event, fn, context);
        }
    }

    private async runStateStep(state: EWinPresentationStates, debugSuffix: string, condition?: { resolve: () => void }): Promise<void> {
        const sublevel = this.currentSublevels[this.currentSubLevelIndex];
        const sublevelName = sublevel.name;

        const step = this.prepareWinPresentationStep(state, sublevel);

        EventsCenter.emit(EDebugWinPresentationEvents.DEBUG_WINPRESENTATION_CHANGE, {
            ...step,
            state: `${sublevelName}${debugSuffix}`
        });

        await this.stepExecutor.execute(step);

        condition?.resolve();
    }

    public update(deltaMS: number): void {
        this.particleEmitters.forEach((emitter) => {
            emitter.update(deltaMS);
        });
    }
}
